// Login.js
import React, { useState, useEffect } from 'react'; 
import { Link, useNavigate, useSearchParams } from 'react-router-dom'; 
import useLocale from '../hooks/useLocale';
import axios from 'axios';
import './login.css';
import { useAuth } from '../context/AuthContext';
import InfoTooltip from '../components/InfoTooltip'; // Adjust path


function Login() {
    const { handleLanguageChange, t } = useLocale();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState(''); // For login results
    const { login } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();



    // --- ADDITIONS START ---
    const [searchParams] = useSearchParams(); // Hook to read query params like ?verified=true
    const [verificationSuccessMessage, setVerificationSuccessMessage] = useState(''); // State specifically for the verification message
    const [passwordResetSuccessMessage, setPasswordResetSuccessMessage] = useState('');

    useEffect(() => {
        const verifiedParam = searchParams.get('verified');
        const resetParam = searchParams.get('reset'); // <-- Use 'reset'
    
        let timerId = null; // Variable for cleanup
    
        // Clear both messages initially unless one is set by query params
        setVerificationSuccessMessage('');
        setPasswordResetSuccessMessage('');
    
        if (verifiedParam === 'true') {
            setVerificationSuccessMessage(
                t('auth_login_verified_success', 'Email successfully verified! Please log in.')
            );
            // Optional timer
            timerId = setTimeout(() => setVerificationSuccessMessage(''), 7000);
        } else if (resetParam === 'success') { // <-- Use 'reset'
            setPasswordResetSuccessMessage(
                t('auth_login_password_reset_success', 'Password reset successfully! Please log in.')
            );
            // Optional timer
            timerId = setTimeout(() => setPasswordResetSuccessMessage(''), 7000);
        }
    
        // Cleanup function for the timer
        return () => {
            if (timerId) {
                clearTimeout(timerId);
            }
        };
    
    }, [searchParams, t]); // Dependencies




    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true); // Disable button at the start
        setMessage('');     // Clear previous messages
        setVerificationSuccessMessage('');
        setPasswordResetSuccessMessage(''); // <-- Make sure this is cleared too
    
        try { // <--- Main try block STARTS
            // Make the API call
            const response = await axios.post('/api/login', { // Or just '/login'
                // Ensure these state variables match your input fields
                username: username, // Use the state variable holding the username/email input
                password: password  // Use the state variable holding the password input
            });
    
            // --- START: New Success Handling Logic ---
            // (Replaces your original: setMessage(response.data.message); )
    
            const token = response.data.token; // Extract token from response
    
            if (token) {
                // Login was successful AND token received
                console.log("[Login.js] Login successful, token received."); // Keep this log
    
                login(token);
    
                // IMPORTANT: Update your app's authentication state here
                // Replace this comment with your actual function call:
    
                // Set success message and initiate redirect
                setMessage(t('API_AUTH_LOGIN_SUCCESS', 'Login successful! Redirecting...'));

                setTimeout(() => {
                    navigate('/home'); // Redirect to dashboard or other protected route
                }, 1000); // 1-second delay
    
                // Note: We DON'T set isLoading=false here because we are redirecting shortly
    
            } else {
                // Login API call was successful (e.g., 200 OK) but didn't return a token
                console.error("Login response OK but no token received.");
                // Use optional chaining for strings if useLocale might not be ready initially
                setMessage(t('API_AUTH_LOGIN_NO_TOKEN', 'Login failed: Authentication incomplete. Please try again.'));                
                setIsLoading(false); // Allow user to try again
            }
            // --- END: New Success Handling Logic ---
    
        } catch (error) { // <--- Main catch block STARTS (handles API errors like 4xx, 5xx)
            console.error("Login API call failed:", error.response || error.message);
            // Set error message based on backend response or fallback
            setMessage(
                error.response?.data?.error || // Use specific error from backend if available
                t('API_SERVER_ERROR_GENERIC', 'An unexpected server error occurred. Please try again later.')
            );
            setIsLoading(false); // Allow user to try again after an error
    
        }
        // NOTE: We removed the final setIsLoading(false) from here.
        // It's now handled specifically within the catch block and the 'else' block (for non-token success).
        // It should NOT run if the success path is redirecting.
    
    }; // <--- Main handleSubmit function ENDS



    return (
        <div>
            {/* Language Buttons */}
            <button onClick={() => handleLanguageChange('en')}>English</button>
            <button onClick={() => handleLanguageChange('es')}>Spanish</button>

            <h2>{t('auth_login_title', 'Login')}</h2>
            {passwordResetSuccessMessage && (
    <p className="form-message success">{passwordResetSuccessMessage}</p>
)}
{verificationSuccessMessage && (
    <p className="form-message success">{verificationSuccessMessage}</p>
)}
            <form onSubmit={handleSubmit} className="login-form">
                {/* Username Field */}
                <div className="form-group">
                    <label htmlFor="username">
                    <InfoTooltip
                         text={t('auth_login_username_hint', 'Username only, Email not accepted')}
                         idSuffix="upload-types"
                         place="top"
                     />
                        {t('auth_login_username_label', 'Username')}:</label>
                    <input
                        type="text"
                        id="username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        required
                    />
                </div>

                {/* Password Field */}
                <div className="form-group">
                    <label htmlFor="password">{t('auth_login_password_label', 'Password')}:</label>
                    <input
                        type="password"
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                </div>

                <button type="submit" disabled={isLoading}>
                    {isLoading ? (t('auth_login_loading_button', 'Logging in...')) : (t('auth_login_submit_button', 'Login'))}
                </button>
            </form> {/* <--- End of form element */}

            {/* Display Messages (Moved outside form) */}
            {message && <p style={{ color: message.includes('successful') ? 'green' : 'red', marginTop: '1rem', textAlign: 'center' }}>{message}</p>}

            {/* --- ADD FORGOT PASSWORD LINK HERE --- */}
            <p style={{ marginTop: '0.5rem', textAlign: 'center' }}> {/* Adjust styling as needed */}
              <Link to="/forgot-password" className="app-link"> {/* Optional: reuse class */}
                {/* Use a key from your strings object */}
                {t('auth_login_forgot_password_link', 'Forgot Password?')}
              </Link>
            </p>
            {/* --- END FORGOT PASSWORD LINK --- */}

            {/* Link to Register (Moved outside form) */}
            <p style={{ marginTop: '1rem', textAlign: 'center' }}>
                {t('auth_login_no_account_prompt', 'Don\'t have an account?')}{' '}
                <Link to="/register" className="app-link">
                    {t('auth_login_register_link', 'Register here')}
                </Link>
            </p>

        </div> // End main div
    );
}

export default Login;